/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { AUX_RELAYS, AuxRelay } from '../../../core/model/aux-relay';

/**
 * WebUSB driver for the Sealevel SeaDAC Lite 8115 USB relay module.
 *
 * The protocol implemented here mirrors Sealevel's own LGPL SeaMAX for Linux driver (seamax/lib/seadaclite.c), which is
 * the authoritative reference for the device.
 */
export class SeaDacLiteDriver {
    public static readonly VENDOR_ID = 0x0c52;
    public static readonly PRODUCT_ID = 0x8115;
    public static readonly DEVICE_NAME = 'SeaDAC Lite 8115';

    /** FTDI vendor control requests. */
    private static readonly SIO_RESET = 0x00;
    private static readonly SIO_SET_BITMODE = 0x0b;
    private static readonly SIO_READ_PINS = 0x0c;
    private static readonly BITMODE_RESET = 0x00;
    private static readonly BITMODE_BITBANG = 0x01;
    private static readonly ALL_PINS_OUTPUT = 0xff; // all eight pins are outputs; no inputs

    private static readonly FTDI_PORT_INDEX = 1;
    private static readonly INTERFACE_NUMBER = 0;
    private static readonly BULK_OUT_ENDPOINT = 2;

    private static readonly RELAY_NIBBLE_MASK = 0xf0; // upper nibble of the byte drives the relays (lower nibble unused)
    private static readonly RELAY_BIT_OFFSET = 4;

    public static readonly BITBANG_SETTLE_MS = 1000; // allow time for the FTDI chip to switch to bit-bang mode
    public static readonly RELAY_SETTLE_MS = 20; // allow time for relay contacts to settle before next write

    private device: USBDevice | undefined;
    private claimed = false;
    private relayMask = 0x00;

    /**
     * Use a single queue so that cached relay mask can never be read and written concurrently.
     */
    private queue: Promise<unknown> = Promise.resolve();

    public get opened(): boolean {
        return Boolean(this.device?.opened) && this.claimed;
    }

    public get currentMask(): number {
        return this.relayMask;
    }

    /**
     * Bit within the output byte that drives the given relay. Relay 1 is the low bit of the upper nibble.
     */
    public static relayBit(relay: AuxRelay): number {
        return 1 << (SeaDacLiteDriver.RELAY_BIT_OFFSET + AUX_RELAYS.indexOf(relay));
    }

    /**
     * Opens the device, arms asynchronous bit-bang mode and leaves every relay de-energized.
     */
    public async open(device: USBDevice): Promise<void> {
        this.device = device;
        if (!device.opened) {
            await device.open();
        }
        if (!device.configuration) {
            await device.selectConfiguration(1);
        }

        await device.claimInterface(SeaDacLiteDriver.INTERFACE_NUMBER);
        this.claimed = true;

        await this.controlOut(SeaDacLiteDriver.SIO_RESET, 0);
        await this.controlOut(SeaDacLiteDriver.SIO_SET_BITMODE, (SeaDacLiteDriver.BITMODE_BITBANG << 8) | SeaDacLiteDriver.ALL_PINS_OUTPUT);
        await SeaDacLiteDriver.delay(SeaDacLiteDriver.BITBANG_SETTLE_MS);

        this.relayMask = 0x00;
        await this.writeMask(this.relayMask);
        console.debug(`${SeaDacLiteDriver.DEVICE_NAME}: bit-bang mode armed on interface ${SeaDacLiteDriver.INTERFACE_NUMBER}, endpoint ${SeaDacLiteDriver.BULK_OUT_ENDPOINT}`);
    }

    public setRelay(relay: AuxRelay, energized: boolean): Promise<void> {
        return this.enqueue(async () => {
            const bit = SeaDacLiteDriver.relayBit(relay);
            const next = energized ? this.relayMask | bit : this.relayMask & ~bit;
            if (next === this.relayMask) {
                return;
            }
            await this.writeMask(next);
            await SeaDacLiteDriver.delay(SeaDacLiteDriver.RELAY_SETTLE_MS); // allow relay contacts time to rest before next write
        });
    }

    public deEnergizeAll(): Promise<void> {
        return this.enqueue(() => this.writeMask(0x00));
    }

    /**
     * De-energizes every relay without waiting for the serialization queue. Intended for page teardown
     */
    public async deEnergizeAllNow(): Promise<void> {
        if (this.opened) {
            await this.writeMask(0x00);
        }
    }

    public readRelayMask(): Promise<number> {
        return this.enqueue(async () => {
            const result = await this.device.controlTransferIn(
                {
                    requestType: 'vendor',
                    recipient: 'device',
                    request: SeaDacLiteDriver.SIO_READ_PINS,
                    value: 0,
                    index: SeaDacLiteDriver.FTDI_PORT_INDEX
                },
                1
            );
            if (result.status !== 'ok' || !result.data?.byteLength) {
                throw new Error(`${SeaDacLiteDriver.DEVICE_NAME}: pin read failed with status '${result.status}'`);
            }
            return result.data.getUint8(0) & SeaDacLiteDriver.RELAY_NIBBLE_MASK;
        });
    }

    public async close(): Promise<void> {
        if (!this.device) {
            return;
        }
        const device = this.device;
        const claimed = this.claimed;
        try {
            if (this.opened) {
                await this.deEnergizeAllNow();
                await this.controlOut(SeaDacLiteDriver.SIO_SET_BITMODE, SeaDacLiteDriver.BITMODE_RESET << 8);
            }
        } catch (e) {
            console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to return device to a safe state`, e);
        }
        try {
            if (claimed) {
                await device.releaseInterface(SeaDacLiteDriver.INTERFACE_NUMBER);
            }
        } catch (e) {
            console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to release the interface`, e);
        }
        try {
            await device.close();
        } catch (e) {
            console.error(`${SeaDacLiteDriver.DEVICE_NAME}: failed to close the device`, e);
        }
        // sanity check if device was re-opened before this close() call completed
        if (this.device === device) {
            this.device = undefined;
            this.claimed = false;
            this.relayMask = 0x00;
        }
    }

    private async writeMask(mask: number): Promise<void> {
        const value = mask & SeaDacLiteDriver.RELAY_NIBBLE_MASK;
        const result = await this.device.transferOut(SeaDacLiteDriver.BULK_OUT_ENDPOINT, new Uint8Array([value]));
        if (result.status !== 'ok') {
            throw new Error(`${SeaDacLiteDriver.DEVICE_NAME}: relay write failed with status '${result.status}'`);
        }
        this.relayMask = value;
    }

    private controlOut(request: number, value: number): Promise<USBOutTransferResult> {
        return this.device.controlTransferOut({
            requestType: 'vendor',
            recipient: 'device',
            request: request,
            value: value,
            index: SeaDacLiteDriver.FTDI_PORT_INDEX
        });
    }

    private enqueue<T>(task: () => Promise<T>): Promise<T> {
        const result = this.queue.then(task);
        this.queue = result.catch(() => undefined);
        return result;
    }

    private static delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
